<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { useStore } from '~/store'
import { useRoute } from 'vue-router'
import { to } from '~/routing'

import useThemeList from '~/composables/useThemeList'
import useTheme from '~/composables/useTheme'

import { useModal } from '~/shared/useModal'

import Button from '~/ui/Button.vue'
import Popover from '~/ui/Popover.vue'
import PopoverItem from '~/ui/popover/PopoverItem.vue'
import PopoverSubmenu from '~/ui/popover/PopoverSubmenu.vue'
import Spacer from '~/ui/Spacer.vue'
import Badge from '~/ui/Badge.vue'

const route = useRoute()
const store = useStore()

const { t } = useI18n()

const themes = useThemeList()
const { theme } = useTheme()

const isOpen = ref(false)
const openPrivate = () => window.location.assign('/private/')
const openInvitations = () => window.location.assign('/invitations/')

const labels = computed(() => ({
  profile: t('components.common.UserMenu.link.profile'),
  settings: t('components.common.UserMenu.link.settings'),
  logout: t('components.common.UserMenu.link.logout'),
  about: t('components.common.UserMenu.link.about'),
  shortcuts: t('components.common.UserMenu.label.shortcuts'),
  support: t('components.common.UserMenu.link.support'),
  forum: t('components.common.UserMenu.link.forum'),
  docs: t('components.common.UserMenu.link.docs'),
  language: t('components.common.UserMenu.label.language'),
  theme: t('components.common.UserMenu.label.theme'),
  chat: t('components.common.UserMenu.link.chat'),
  git: t('components.common.UserMenu.link.git'),
  login: t('components.common.UserMenu.link.login'),
  signup: t('components.common.UserMenu.link.signup'),
  notifications: t('components.common.UserMenu.link.notifications'),
  uploads: t('views.auth.ProfileBase.link.manageUploads'),
  shareLinks: t('views.auth.ProfileBase.link.manageSharelinks')

}))
</script>

<template>
  <Popover
    v-model="isOpen"
  >
    <!-- TODO: Replace with Toggle component once that's done, and use v-model -->
    <Button
      :aria-pressed="isOpen"
      aria-label="用户菜单"
      class="color-default-solid shape-circle"
      ui-block-size="40"
      ui-raised
      @click="isOpen = !isOpen"
    >
      <Badge
        v-if="store.state.auth.authenticated && store.state.ui.notifications.inbox > 0"
        ui-raised
      >
        {{ store.state.ui.notifications.inbox }}
      </Badge>
      <img
        v-if="store.state.auth.authenticated && store.state.auth.profile?.avatar?.urls.small_square_crop"
        alt=""
        :src="store.getters['instance/absoluteUrl'](store.state.auth.profile?.avatar.urls.small_square_crop)"
        class="avatar"
      >
      <span
        v-else-if="store.state.auth.authenticated"
      >
        <Icon icon="bi:person-circle" height="42" style="padding-top: 4px;" />
      </span>
      <Icon
        v-else
        icon="bi:gear-fill"
      />
    </Button>
    <template #items>
      <PopoverItem
        v-if="store.state.auth.authenticated"
        :to="{ name: 'profile.content', params: { username: store.state.auth.username }, }"
        icon="bi:person-fill"
      >
        {{ labels.profile }}
      </PopoverItem>
      <PopoverItem
        v-if="store.state.auth.authenticated"
        :to="{ name: 'notifications' }"
        icon="bi:inbox-fill"
      >
        {{ labels.notifications }}
        <Spacer grow />
        <div
          v-if="store.state.ui.notifications.inbox > 0"
          :title="labels.notifications"
          style="
            background: var(--fw-gray-400);
            color: var(--fw-gray-800);
            padding: 2px 7px;
            border-radius: 10px;
            font-size: 12px;
          "
        >
          {{ store.state.ui.notifications.inbox }}
        </div>
      </PopoverItem>
      <PopoverItem
        v-if="store.state.auth.authenticated"
        :to="{ name: 'profile.manageUploads', params: { username: store.state.auth.username }, }"
      >
        <Icon icon="bi:cloud-upload-fill" />
        {{ labels.uploads }}
      </PopoverItem>
      <PopoverItem
        v-if="store.state.auth.authenticated"
        :to="{ name: 'profile.sharelinks', params: { username: store.state.auth.username }, }"
      >
        <Icon icon="bi:link-45deg" />
        {{ labels.shareLinks }}
      </PopoverItem>
      <PopoverItem
        v-if="store.state.auth.authenticated"
        :to="{ name: 'settings' }"
      >
        <Icon icon="bi:gear-fill" />
        {{ labels.settings }}
      </PopoverItem>
      <PopoverItem
        v-if="store.state.auth.authenticated"
        icon="bi:person-plus-fill"
        @click="openInvitations"
      >
        邀请好友
      </PopoverItem>
      <PopoverItem
        v-if="store.state.auth.authenticated"
        icon="bi:lock-fill"
        @click="openPrivate"
      >
        我的私有内容
      </PopoverItem>
      <hr v-if="store.state.auth.authenticated">
      <PopoverItem v-bind="to(useModal('language').to)">
        <Icon icon="bi:translate" />
        {{ `${labels.language}...` }}
      </PopoverItem>
      <PopoverSubmenu>
        <Icon icon="bi:palette-fill" />
        {{ labels.theme }}
        <template #items>
          <PopoverItem
            v-for="th in themes"
            :key="th.key"
            @click="theme = th.key"
          >
            <Icon :icon="th.icon" />
            {{ th.name }}
          </PopoverItem>
        </template>
      </PopoverSubmenu>
      <hr>
      <PopoverSubmenu>
        <Icon icon="bi:question-square-fill" />
        {{ labels.support }}
        <template #items>
          <PopoverItem to="https://matrix.to/#/#funkwhale-support:matrix.org">
            <Icon icon="bi:chat-left-fill" />
            {{ labels.chat }}
          </PopoverItem>
          <PopoverItem to="https://dev.funkwhale.audio/funkwhale/funkwhale/issues">
            <Icon icon="bi:gitlab" />
            {{ labels.git }}
          </PopoverItem>
        </template>
      </PopoverSubmenu>
      <PopoverItem to="https://docs.funkwhale.audio">
        <Icon icon="bi:book" />
        {{ labels.docs }}
      </PopoverItem>
      <PopoverItem v-bind="to(useModal('shortcuts').to)">
        <Icon icon="bi:keyboard" />
        {{ labels.shortcuts }}
      </PopoverItem>
      <hr v-if="store.state.auth.authenticated">
      <PopoverItem
        v-if="store.state.auth.authenticated && route.path !== '/logout'"
        v-bind="to({ name: 'logout' })"
      >
        <Icon icon="bi:box-arrow-right" />
        {{ labels.logout }}
      </PopoverItem>
      <PopoverItem
        v-if="!store.state.auth.authenticated && route.path !== '/login'"
        v-bind="to({ name: 'login' })"
      >
        <Icon icon="bi:box-arrow-in-right" />
        {{ labels.login }}
      </PopoverItem>
      <PopoverItem
        v-if="!store.state.auth.authenticated && store.state.instance.settings.users.registration_enabled.value"
        v-bind="to({ name: 'signup' })"
      >
        <Icon icon="bi:person-square" />
        {{ labels.signup }}
      </PopoverItem>
    </template>
  </Popover>
</template>

<style scoped>
.avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
}

nav.button-list {
  width: 100%;
}
</style>
